import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as projectsApi from "@/api/projects.api";
import type { Project } from "@/types";

interface ProjectsState {
  items: Project[];
  current: Project | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProjectsState = {
  items: [],
  current: null,
  loading: false,
  error: null,
};

export const fetchProjects = createAsyncThunk("projects/fetchAll", async () =>
  projectsApi.getProjects(),
);

export const fetchProject = createAsyncThunk(
  "projects/fetchOne",
  async (id: string) => projectsApi.getProject(id),
);

export const createProject = createAsyncThunk(
  "projects/create",
  async (data: Partial<Project>) => projectsApi.createProject(data),
);

export const deleteProject = createAsyncThunk(
  "projects/delete",
  async (id: string) => {
    await projectsApi.deleteProject(id);
    return id;
  },
);

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    clearCurrent(state) {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch projects";
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.items = state.items.filter((p) => p.id !== action.payload);
        if (state.current?.id === action.payload) state.current = null;
      });
  },
});

export const { clearCurrent } = projectsSlice.actions;
export default projectsSlice.reducer;
